'use client';

import React, { type ReactNode } from 'react';
import { useColors } from '../theme/ThemeProvider';
import { motion } from '../theme/tokens';

export interface RingProps {
  /** Current value. */
  value: number;
  /** Target/max value. */
  max: number;
  /** Outer diameter in px. Defaults to 72. */
  size?: number;
  /** Ring stroke width. Defaults to 6. */
  stroke?: number;
  /** Override accent color (defaults to theme accent). */
  color?: string;
  /** Rendered in the center of the ring. */
  children?: ReactNode;
}

/**
 * Circular progress ring, SVG-based. Smoothly animates strokeDashoffset.
 */
export function Ring({
  value,
  max,
  size = 72,
  stroke = 6,
  color,
  children,
}: RingProps) {
  const colors = useColors();
  const ringColor = color ?? colors.accent;

  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(1, Math.max(0, value / max));
  const offset = circumference * (1 - progress);

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={colors.border}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: `stroke-dashoffset ${motion.duration.slower}ms ${motion.easing.inOut}, stroke ${motion.duration.base}ms`,
          }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children}
      </div>
    </div>
  );
}
